import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Text } from '@react-three/drei';
import * as THREE from 'three';

const panelTypes = [
  {
    id: 1,
    name: "Panel estándar (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.0125,
    weight: 29
  },
  {
    id: 2,
    name: "Panel resistente (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.015,
    weight: 35
  },
  {
    id: 3,
    name: "Panel insonorizado (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.018,
    weight: 42
  }
];

const metalDensity = 7.85; // kg/m³ (acero)
const screwWeight = 0.005; // kg por tornillo

const DrywallCalculator = () => {
  const [wallDimensions, setWallDimensions] = useState({
    length: 4.8,
    height: 2.4,
    thickness: 0.1
  });

  const [selectedPanel, setSelectedPanel] = useState(panelTypes[0]);
  const [metalStudSpacing, setMetalStudSpacing] = useState(0.6);
  const [metalStudThickness, setMetalStudThickness] = useState(0.048);
  const [singleSide, setSingleSide] = useState(false);
  const [includeFoam, setIncludeFoam] = useState(false);
  const [showEdges, setShowEdges] = useState(true);
  
  const [results, setResults] = useState({
    panels: [],
    metalStuds: [],
    runners: [],
    screws: 0,
    totalWeight: 0,
    weightPerMeter: 0,
    metalWeight: 0,
    panelsWeight: 0,
    screwsWeight: 0,
    panelCutPieces: [],
    studCutPieces: []
  });

  useEffect(() => {
    calculateMaterials();
  }, [wallDimensions, selectedPanel, metalStudSpacing, metalStudThickness, singleSide, includeFoam]);

  const calculateMaterials = () => {
    const { length = 0, height = 0, thickness = 0 } = wallDimensions;
    const { 
      width: panelW = 1.2, 
      height: panelH = 2.4, 
      thickness: panelT = 0.0125, 
      weight: panelWeight = 29 
    } = selectedPanel || {};

    const studSpacing = metalStudSpacing || 0.6;
    const studThickness = metalStudThickness || 0.048;

    // Calculate panels
    const panelCols = Math.ceil(length / panelW);
    const panelRows = Math.ceil(height / panelH);
    const panelArea = panelW * panelH;
    let panelsWeight = 0;
    let panelCutPieces = [];
    let fullPanels = 0;

    for (let row = 0; row < panelRows; row++) {
      for (let col = 0; col < panelCols; col++) {
        const remainingHeight = height - row * panelH;
        const remainingWidth = length - col * panelW;
        const panelHeight = Math.min(panelH, remainingHeight);
        const panelWidth = Math.min(panelW, remainingWidth);
        
        const areaRatio = (panelWidth * panelHeight) / panelArea;
        const pieceWeight = panelWeight * areaRatio;
        panelsWeight += pieceWeight;

        if (panelWidth === panelW && panelHeight === panelH) {
          fullPanels++;
        } else {
          panelCutPieces.push({
            width: panelWidth,
            height: panelHeight,
            weight: pieceWeight
          });
        }
      }
    }

    // Adjust for single side
    const sideMultiplier = singleSide ? 1 : 2;
    fullPanels *= sideMultiplier;
    panelCutPieces = panelCutPieces.concat(singleSide ? [] : panelCutPieces.map(p => ({...p})));
    panelsWeight *= sideMultiplier;

    // Calculate metal studs
    const studsCount = Math.ceil(length / studSpacing) + 1;
    const runnerRows = Math.ceil(height / 0.6) * 2;
    let metalWeight = 0;
    let studCutPieces = [];

    // Vertical studs
    for (let i = 0; i < studsCount; i++) {
      const posX = -length/2 + i * studSpacing;
      if (posX > length/2) continue;
      
      const studLength = height;
      const studVolume = studLength * studThickness * 0.5;
      const studWeight = studVolume * metalDensity;
      metalWeight += studWeight;

      studCutPieces.push({
        length: studLength,
        position: posX,
        weight: studWeight
      });
    }

    // Horizontal runners (top and bottom)
    const runnerVolume = length * studThickness * 0.5;
    const runnerWeight = runnerVolume * metalDensity;
    metalWeight += runnerWeight * runnerRows;

    // Screws
    const screws = Math.ceil(length * height * 4 * sideMultiplier);
    const screwsWeight = screws * screwWeight;
    const totalWeight = panelsWeight + metalWeight + screwsWeight;
    const weightPerMeter = length > 0 ? totalWeight / length : 0;

    setResults({
      panels: Array(fullPanels).fill({ width: panelW, height: panelH }),
      metalStuds: studCutPieces,
      runners: Array(runnerRows).fill({ length: length }),
      screws: screws,
      totalWeight: totalWeight,
      weightPerMeter: weightPerMeter,
      metalWeight: metalWeight,
      panelsWeight: panelsWeight,
      screwsWeight: screwsWeight,
      panelCutPieces: panelCutPieces,
      studCutPieces: studCutPieces
    });
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setWallDimensions(prev => ({
      ...prev,
      [name]: Math.max(0.1, parseFloat(value) || 0)
    }));
  };

  const handleStudSpacingChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setMetalStudSpacing(Math.max(0.4, Math.min(1.2, value)));
    }
  };

  const handleStudThicknessChange = (e) => {
    const thickness = parseInt(e.target.value);
    setMetalStudThickness(thickness === 70 ? 0.07 : thickness === 90 ? 0.09 : 0.048);
  };

  const formatDimension = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
  };

  const renderMaterialList = (items, type) => {
    const counts = {};
    items.forEach(item => {
      const width = formatDimension(item.width || item.length);
      const height = formatDimension(item.height || 2.4);
      const key = `${width}x${height}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([size, count]) => {
      const [width, height] = size.split('x');
      const isFullPanel = type === 'panel' && width === '1.2' && height === '2.4';
      const isFullStud = type === 'stud' && height === '2.4';
      
      return (
        <p key={size} className="text-sm">
          <span className="font-semibold">
            {type === 'panel' ? `Panel ${width} x ${height}m` : `Perfil ${width} x ${height}m`}
            {!isFullPanel && !isFullStud && ' (cortado)'}:
          </span> {count}
        </p>
      );
    });
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center">Calculadora de Drywall</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Parámetros Pared Gypsum</h2>
          
          <div className="space-y-4">
            {['length', 'height', 'thickness'].map((dim) => (
              <div key={dim}>
                <label className="block text-sm font-medium text-gray-700">
                  {dim === 'length' ? 'Longitud' : dim === 'height' ? 'Altura' : 'Espesor'} (m)
                </label>
                <input
                  type="number"
                  name={dim}
                  value={wallDimensions[dim]}
                  onChange={handleDimensionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  min="0.5"
                  step="0.1"
                />
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de panel</label>
              <select
                value={selectedPanel.id}
                onChange={(e) => setSelectedPanel(panelTypes.find(p => p.id === parseInt(e.target.value)) || panelTypes[0])}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                {panelTypes.map(panel => (
                  <option key={panel.id} value={panel.id}>{panel.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Separación entre montantes (m)</label>
              <input
                type="number"
                value={metalStudSpacing}
                onChange={handleStudSpacingChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                min="0.4"
                max="1.2"
                step="0.1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Espesor montantes</label>
              <select
                value={metalStudThickness * 1000}
                onChange={handleStudThicknessChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                <option value="48">48mm</option>
                <option value="70">70mm</option>
                <option value="90">90mm</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="singleSide"
                checked={singleSide}
                onChange={(e) => setSingleSide(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="singleSide" className="ml-2 block text-sm text-gray-700">
                Solo un lado de gypsum
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeFoam"
                checked={includeFoam}
                onChange={(e) => setIncludeFoam(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="includeFoam" className="ml-2 block text-sm text-gray-700">
                Incluir esponja aislante
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showEdges"
                checked={showEdges}
                onChange={(e) => setShowEdges(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="showEdges" className="ml-2 block text-sm text-gray-700">
                Mostrar filos de paneles
              </label>
            </div>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Resultados</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              <h4 className="font-semibold">Paneles:</h4>
              {renderMaterialList(results.panels.concat(results.panelCutPieces), 'panel')}
              
              <h4 className="font-semibold mt-2">Montantes metálicos:</h4>
              {renderMaterialList(results.metalStuds, 'stud')}
              
              <h4 className="font-semibold mt-2">Canales:</h4>
              <p className="text-sm"><span className="font-semibold">Canal superior/inferior:</span> {results.runners.length}</p>
              
              <h4 className="font-semibold mt-2">Otros:</h4>
              <p className="text-sm"><span className="font-semibold">Tornillos estimados:</span> {results.screws}</p>
              <p className="text-sm"><span className="font-semibold">Peso total:</span> {formatDimension(results.totalWeight)} kg</p>
              <p className="text-sm"><span className="font-semibold">Peso por metro lineal:</span> {formatDimension(results.weightPerMeter)} kg/m</p>
              <p className="text-sm"><span className="font-semibold">Peso paneles:</span> {formatDimension(results.panelsWeight)} kg</p>
              <p className="text-sm"><span className="font-semibold">Peso estructura:</span> {formatDimension(results.metalWeight)} kg</p>
              <p className="text-sm"><span className="font-semibold">Peso tornillos:</span> {formatDimension(results.screwsWeight)} kg</p>
              {includeFoam && (
                <p className="text-sm"><span className="font-semibold">Esponja aislante:</span> {formatDimension(wallDimensions.length * wallDimensions.height)} m²</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Visualización 3D</h2>
          <div className="h-[700px] border border-gray-200 rounded-md">
            <Canvas>
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
              <PerspectiveCamera makeDefault position={[5, 5, 5]} />
              <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
              <Drywall3D 
                wall={wallDimensions} 
                panel={selectedPanel} 
                studSpacing={metalStudSpacing}
                studThickness={metalStudThickness}
                singleSide={singleSide}
                includeFoam={includeFoam}
                showEdges={showEdges}
              />
            </Canvas>
          </div>
        </div>
      </div>
    </div>
  );
};

const Drywall3D = ({ wall, panel, studSpacing, studThickness, singleSide, includeFoam, showEdges }) => {
  const { length = 4.8, height = 2.4, thickness = 0.1 } = wall;
  const { width: panelW = 1.2, height: panelH = 2.4, thickness: panelT = 0.0125 } = panel || {};

  const lengthTextRef = useRef();
  const heightTextRef = useRef();

  useFrame(({ camera }) => {
    if (lengthTextRef.current) lengthTextRef.current.lookAt(camera.position);
    if (heightTextRef.current) heightTextRef.current.lookAt(camera.position);
  });

  // Estructura metálica
  const metalStructure = [];
  const studsCount = Math.ceil(length / studSpacing) + 1;
  const actualStudThickness = studThickness || 0.048;
  const halfStudThickness = actualStudThickness / 2;
  
  // Montantes verticales
  for (let i = 0; i < studsCount; i++) {
    const posX = -length/2 + i * studSpacing;
    if (posX > length/2) continue;
    
    metalStructure.push(
      <mesh key={`stud-${i}`} position={[posX, 0, 0]}>
        <boxGeometry args={[actualStudThickness, height - actualStudThickness, actualStudThickness]} />
        <meshStandardMaterial color="#cccccc" metalness={0.8} roughness={0.4} />
      </mesh>
    );
  }

  // Canales
  metalStructure.push(
    <mesh 
      key="top-runner" 
      position={[0, height/2 - halfStudThickness, 0]} 
      rotation={[Math.PI/2, 0, 0]}
    >
      <boxGeometry args={[length, actualStudThickness, actualStudThickness]} />
      <meshStandardMaterial color="#aaaaaa" metalness={0.8} roughness={0.4} />
    </mesh>
  );

  metalStructure.push(
    <mesh 
      key="bottom-runner" 
      position={[0, -height/2 + halfStudThickness, 0]} 
      rotation={[Math.PI/2, 0, 0]}
    >
      <boxGeometry args={[length, actualStudThickness, actualStudThickness]} />
      <meshStandardMaterial color="#aaaaaa" metalness={0.8} roughness={0.4} />
    </mesh>
  );

  // Función para crear paneles con bordes visibles
  const createPanel = (posX, posY, width, height, thickness, positionZ, key) => {
    return (
      <group key={key}>
        <mesh position={[posX, posY, positionZ]}>
          <boxGeometry args={[width, height, thickness]} />
          <meshStandardMaterial 
            color="#f5f5f5" 
            roughness={0.8}
            transparent
            opacity={0.7}
          />
        </mesh>
        {showEdges && (
          <lineSegments position={[posX, posY, positionZ]}>
            <edgesGeometry args={[new THREE.BoxGeometry(width, height, thickness)]} />
            <lineBasicMaterial color="#333333" linewidth={4} />
          </lineSegments>
        )}
      </group>
    );
  };

  // Función para crear esponja aislante
  const createFoam = (posX, posY, width, height, positionZ, key) => {
    return (
      <mesh 
        key={key} 
        position={[posX, posY, positionZ - panelT/2 - 0.01]}
      >
        <boxGeometry args={[width, height, 0.02]} />
        <meshStandardMaterial 
          color="#ff0000" 
          roughness={0.9}
          transparent
          opacity={0.5}
        />
      </mesh>
    );
  };

  // Paneles frontales y traseros
  const panels = [];
  const panelRows = Math.ceil(height / panelH);
  const panelCols = Math.ceil(length / panelW);

  const sides = singleSide ? [0] : [0, 1];
  
  sides.forEach(side => {
    const zPos = side === 0 ? 
      thickness/2 + panelT/2 : 
      -thickness/2 - panelT/2;
    const prefix = side === 0 ? 'front' : 'back';

    for (let row = 0; row < panelRows; row++) {
      for (let col = 0; col < panelCols; col++) {
        const remainingHeight = height - row * panelH;
        const remainingWidth = length - col * panelW;
        
        if (remainingHeight <= 0 || remainingWidth <= 0) continue;

        const panelHeight = Math.min(panelH, remainingHeight);
        const panelWidth = Math.min(panelW, remainingWidth);
        
        const posX = -length/2 + col * panelW + panelWidth/2;
        const posY = -height/2 + row * panelH + panelHeight/2;

        panels.push(
          createPanel(
            posX,
            posY,
            panelWidth,
            panelHeight,
            panelT,
            zPos,
            `${prefix}-panel-${row}-${col}`
          )
        );

        // Añadir esponja si está habilitado y es el lado frontal
        if (includeFoam && side === 0) {
          panels.push(
            createFoam(
              posX,
              posY,
              panelWidth,
              panelHeight,
              zPos,
              `${prefix}-foam-${row}-${col}`
            )
          );
        }
      }
    }
  });

  return (
    <group>
      {/* Medidas de la pared */}
      <group ref={lengthTextRef} position={[0, -height/2 - 0.25, 0]}>
        <Text 
          color="black" 
          anchorX="center" 
          anchorY="middle" 
          fontSize={0.1}
          maxWidth={2}
          overflowWrap="break-word"
        >
          {length.toFixed(2)} m
        </Text>
      </group>
      
      <group ref={heightTextRef} position={[-length/2 - 0.25, 0, 0]}>
        <Text 
          color="black" 
          anchorX="center" 
          anchorY="middle" 
          fontSize={0.1}
          rotation={[0, 0, Math.PI/2]}
          maxWidth={2}
          overflowWrap="break-word"
        >
          {height.toFixed(2)} m
        </Text>
      </group>
      
      {metalStructure}
      {panels}
    </group>
  );
};

export default DrywallCalculator;